// Shared between punches.js (PIN/email submit) and qr_scanner.js (QR submit)
// -- both are separate entry points that ultimately submit the same
// #punch-form, and a remote/in-home-care staff member might use either one.
// Populates hidden latitude/longitude fields from the device's own GPS
// before submitting, so ClockPunch can verify a remote-allowed staff's
// location; never blocks or fails the punch if location is unavailable.
const COOLDOWN_KEY = 'geoUnavailableUntil';
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

function withinCooldown() {
    try {
        const until = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
        return Date.now() < until;
    } catch (e) {
        return false; // localStorage unavailable (private mode, etc.) -- just try again
    }
}

function startCooldown() {
    try {
        localStorage.setItem(COOLDOWN_KEY, String(Date.now() + COOLDOWN_MS));
    } catch (e) {
        // Ignored -- worst case we prompt again next time.
    }
}

function setHiddenCoordinates(form, latitude, longitude) {
    const latField = form.querySelector('#latitude');
    const lonField = form.querySelector('#longitude');
    if (latField) latField.value = latitude ?? '';
    if (lonField) lonField.value = longitude ?? '';
}

// Always calls onDone() exactly once, whether or not a position was
// captured -- the punch must never be blocked waiting on geolocation.
export function captureLocationThenSubmit(form, onDone) {
    if (!navigator.geolocation || withinCooldown()) {
        onDone();
        return;
    }

    navigator.geolocation.getCurrentPosition(
        (position) => {
            setHiddenCoordinates(form, position.coords.latitude, position.coords.longitude);
            onDone();
        },
        () => {
            // Denied, unavailable, or timed out -- don't nag a shared on-site
            // kiosk device (its IP is whitelisted, so this data goes unused
            // anyway) on every walk-up punch.
            startCooldown();
            onDone();
        },
        { timeout: 5000, maximumAge: 120000, enableHighAccuracy: false }
    );
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
}

// Asks the server (over AJAX, after GPS is already in the hidden fields)
// whether this punch would be flagged, without creating one -- returns a
// Promise resolving to the warning message, or null if there's nothing to
// warn about. Fails open (resolves null) on any network error: this is only
// a courtesy heads-up, never a gate -- the real, authoritative check still
// runs when the punch is actually submitted.
function checkLocationWarning(form) {
    return fetch('/punches/check_location', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: new FormData(form),
    })
        .then((response) => (response.ok ? response.json() : { warning: null }))
        .then((data) => data.warning)
        .catch(() => null);
}

// Captures GPS, then -- if that puts the punch outside the staff's
// permitted range -- asks the user to confirm before actually submitting.
// Calling onSubmit() completes the punch exactly as before; onCancel() lets
// the caller undo whatever "processing..." UI it set up, since the user
// chose not to continue after seeing the warning.
export function confirmLocationThenSubmit(form, onSubmit, onCancel) {
    captureLocationThenSubmit(form, () => {
        checkLocationWarning(form).then((warning) => {
            if (!warning || confirm(warning)) {
                onSubmit();
            } else if (onCancel) {
                onCancel();
            }
        });
    });
}
